// src/pages/Analytics.js
import { useState, useEffect } from "react";
import { getDashboardStats, getAllShipments, getRiders } from "../services/api";

const BAR_COLORS = ["#00C6D7", "#00A896", "#0B1E3D", "#1A3460", "#6B7280"];

export default function Analytics() {
  const [stats, setStats]     = useState(null);
  const [orders, setOrders]   = useState([]);
  const [riders, setRiders]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod]   = useState("all");

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    const [s, o, r] = await Promise.all([getDashboardStats(), getAllShipments(1, ""), getRiders()]);
    if (s?.success) setStats(s.stats);
    if (o?.success) setOrders(o.shipments || []);
    if (r?.success) setRiders(r.riders || []);
    setLoading(false);
  };

  // ── Compute analytics from orders ──────────────────────────────────
  const statusCounts = orders.reduce((acc, o) => {
    acc[o.status] = (acc[o.status] || 0) + 1;
    return acc;
  }, {});

  const routeCounts = orders.reduce((acc, o) => {
    const sender = (o.sender_address || "").toLowerCase();
    const recipient = (o.recipient_address || "").toLowerCase();
    const phKw = ["port harcourt","ph","waterlines","trans amadi","mile","rumuola","gra"];
    const lagosKw = ["lagos","ikeja","lekki","vi","surulere","jibowu","yaba","apapa"];
    const senderPH    = phKw.some(k => sender.includes(k));
    const senderLagos = lagosKw.some(k => sender.includes(k));
    const recipientPH    = phKw.some(k => recipient.includes(k));
    const recipientLagos = lagosKw.some(k => recipient.includes(k));
    let route = "Other";
    if (senderPH && recipientPH) route = "Within PH";
    else if (senderLagos && recipientLagos) route = "Within Lagos";
    else if ((senderPH && recipientLagos) || (senderLagos && recipientPH)) route = "Interstate";
    acc[route] = (acc[route] || 0) + 1;
    return acc;
  }, {});

  const typeCounts = orders.reduce((acc, o) => {
    const t = o.shipment_type || "standard";
    acc[t] = (acc[t] || 0) + 1;
    return acc;
  }, {});

  // Orders by day (last 7 days)
  const last7 = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const dateStr = d.toISOString().split("T")[0];
    const label = d.toLocaleDateString("en-NG", { weekday: "short" });
    const count = orders.filter(o => o.created_at?.startsWith(dateStr)).length;
    last7.push({ label, count, date: dateStr });
  }

  const maxDay = Math.max(...last7.map(d => d.count), 1);

  // Rider performance
  const riderPerf = riders.map(r => ({
    ...r,
    deliveries: orders.filter(o => o.rider_name === r.name && o.status === "delivered").length,
    assigned: orders.filter(o => o.rider_name === r.name).length,
  })).sort((a, b) => b.deliveries - a.deliveries);

  const statusLabels = {
    delivered: "Delivered", in_transit: "In Transit", pending: "Pending",
    picked_up: "Picked Up", out_for_delivery: "Out for Delivery", cancelled: "Cancelled",
  };
  const statusColors = {
    delivered: "#16A34A", in_transit: "#00A896", pending: "#D97706",
    picked_up: "#D97706", out_for_delivery: "#00C6D7", cancelled: "#DC2626",
  };

  const totalOrders = orders.length || 1;

  if (loading) return <div style={{ background: "#F7FAFB", minHeight: "100vh" }}><div className="spinner" /></div>;

  return (
    <div style={{ background: "#F7FAFB", minHeight: "100vh" }}>

      {/* Header */}
      <div style={{ background: "linear-gradient(135deg,#0B1E3D,#1A3460)", padding: "32px 24px 48px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
          <div>
            <span className="tag tag-dark" style={{ marginBottom: 10, display: "inline-block" }}>ANALYTICS</span>
            <h1 style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 28, color: "#fff" }}>Performance Dashboard</h1>
          </div>
          <button className="btn" onClick={loadAll} style={{ background: "rgba(255,255,255,.1)", color: "#fff", border: "1px solid rgba(255,255,255,.15)", padding: "9px 18px", fontSize: 13 }}>
            🔄 Refresh
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 24px 40px" }}>

        {/* KPI Cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 16, marginTop: -24, marginBottom: 28 }}>
          {[
            { label: "Total Orders",       value: stats?.totalOrders ?? orders.length,    color: "#00C6D7", bg: "rgba(0,198,215,.1)", icon: "📦" },
            { label: "Delivered",          value: statusCounts.delivered || 0,            color: "#16A34A", bg: "#DCFCE7",            icon: "✅" },
            { label: "In Transit",         value: stats?.inTransit ?? 0,                  color: "#D97706", bg: "#FEF3C7",            icon: "🚚" },
            { label: "Pending",            value: stats?.pending ?? 0,                    color: "#DC2626", bg: "#FEE2E2",            icon: "⏳" },
            { label: "Active Riders",      value: riders.filter(r => r.is_active).length, color: "#00A896", bg: "rgba(0,168,150,.1)", icon: "🏍️" },
          ].map(k => (
            <div key={k.label} className="card" style={{ boxShadow: "0 4px 16px rgba(0,0,0,.08)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                <div style={{ fontSize: 12, color: "#6B7280", fontWeight: 500 }}>{k.label}</div>
                <div style={{ width: 28, height: 28, borderRadius: 7, background: k.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12 }}>{k.icon}</div>
              </div>
              <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 28, color: k.color }}>{k.value}</div>
            </div>
          ))}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>

          {/* Orders last 7 days */}
          <div className="card">
            <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 16, color: "#0B1E3D", marginBottom: 20 }}>
              Orders — Last 7 Days
            </div>
            {last7.every(d => d.count === 0) ? (
              <div style={{ textAlign: "center", padding: "32px 0", color: "#9CA3AF", fontSize: 13 }}>
                No orders in the last 7 days yet.<br/>Orders will appear here as they come in.
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 160 }}>
                {last7.map((d, i) => (
                  <div key={d.label} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                    <div style={{ fontSize: 11, color: "#6B7280", fontFamily: "'JetBrains Mono',monospace" }}>{d.count || ""}</div>
                    <div style={{
                      width: "100%", borderRadius: "4px 4px 0 0",
                      height: d.count ? `${Math.max((d.count / maxDay) * 120, 4)}px` : "4px",
                      background: d.count ? `linear-gradient(180deg,#00C6D7,#00A896)` : "#E2EBF0",
                      transition: "height .3s"
                    }} />
                    <div style={{ fontSize: 11, color: "#9CA3AF" }}>{d.label}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Orders by status */}
          <div className="card">
            <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 16, color: "#0B1E3D", marginBottom: 20 }}>
              Orders by Status
            </div>
            {Object.keys(statusCounts).length === 0 ? (
              <div style={{ textAlign: "center", padding: "32px 0", color: "#9CA3AF", fontSize: 13 }}>No order data yet.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {Object.entries(statusCounts).map(([status, count]) => (
                  <div key={status}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                      <span style={{ fontSize: 13, color: "#374151", textTransform: "capitalize" }}>{statusLabels[status] || status}</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: statusColors[status] || "#6B7280" }}>{count}</span>
                    </div>
                    <div style={{ height: 8, background: "#E2EBF0", borderRadius: 4, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${(count / totalOrders) * 100}%`, background: statusColors[status] || "#6B7280", borderRadius: 4, transition: "width .5s" }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>

          {/* Route breakdown */}
          <div className="card">
            <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 16, color: "#0B1E3D", marginBottom: 20 }}>
              Orders by Route
            </div>
            {Object.keys(routeCounts).length === 0 ? (
              <div style={{ textAlign: "center", padding: "32px 0", color: "#9CA3AF", fontSize: 13 }}>No route data yet.</div>
            ) : (
              <div>
                {Object.entries(routeCounts).map(([route, count], i) => (
                  <div key={route} style={{ marginBottom: 16 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                      <span style={{ fontSize: 13, color: "#374151", fontWeight: 500 }}>{route}</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: BAR_COLORS[i % BAR_COLORS.length] }}>
                        {count} ({Math.round((count / totalOrders) * 100)}%)
                      </span>
                    </div>
                    <div style={{ height: 10, background: "#E2EBF0", borderRadius: 5, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${(count / totalOrders) * 100}%`, background: BAR_COLORS[i % BAR_COLORS.length], borderRadius: 5 }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Shipment types */}
          <div className="card">
            <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 16, color: "#0B1E3D", marginBottom: 20 }}>
              Shipment Types
            </div>
            {Object.keys(typeCounts).length === 0 ? (
              <div style={{ textAlign: "center", padding: "32px 0", color: "#9CA3AF", fontSize: 13 }}>No shipment data yet.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {Object.entries(typeCounts).sort((a, b) => b[1] - a[1]).map(([type, count], i) => (
                  <div key={type} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 48, height: 48, borderRadius: 10, background: `${BAR_COLORS[i % BAR_COLORS.length]}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>
                      {type === "standard" ? "📦" : type === "express" ? "⚡" : type === "freight" ? "🚛" : type === "fragile" ? "⚠️" : "📄"}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <span style={{ fontSize: 13, color: "#374151", textTransform: "capitalize", fontWeight: 500 }}>{type}</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: BAR_COLORS[i % BAR_COLORS.length] }}>{count}</span>
                      </div>
                      <div style={{ height: 6, background: "#E2EBF0", borderRadius: 3, overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${(count / totalOrders) * 100}%`, background: BAR_COLORS[i % BAR_COLORS.length], borderRadius: 3 }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Rider Performance */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 16, color: "#0B1E3D", marginBottom: 20 }}>
            🏍️ Rider Performance
          </div>
          {riderPerf.length === 0 ? (
            <div style={{ textAlign: "center", padding: "32px 0", color: "#9CA3AF", fontSize: 13 }}>No riders added yet.</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Rider</th>
                    <th>Status</th>
                    <th>Assigned</th>
                    <th>Delivered</th>
                    <th>Completion Rate</th>
                    <th>Performance</th>
                  </tr>
                </thead>
                <tbody>
                  {riderPerf.map(r => {
                    const rate = r.assigned > 0 ? Math.round((r.deliveries / r.assigned) * 100) : 0;
                    return (
                      <tr key={r.id}>
                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg,#00C6D7,#00A896)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, color: "#fff", fontSize: 13 }}>
                              {r.name[0]}
                            </div>
                            <div>
                              <div style={{ fontWeight: 600, color: "#0B1E3D", fontSize: 13 }}>{r.name}</div>
                              <div style={{ fontSize: 11, color: "#9CA3AF", fontFamily: "'JetBrains Mono',monospace" }}>{r.phone || r.email}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span style={{ padding: "3px 8px", borderRadius: 20, fontSize: 10, fontWeight: 600, background: r.is_active ? "#DCFCE7" : "#E2EBF0", color: r.is_active ? "#16A34A" : "#6B7280" }}>
                            {r.is_active ? "ACTIVE" : "INACTIVE"}
                          </span>
                        </td>
                        <td style={{ color: "#374151", fontWeight: 600 }}>{r.assigned}</td>
                        <td style={{ color: "#16A34A", fontWeight: 600 }}>{r.deliveries}</td>
                        <td style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 12, color: rate >= 80 ? "#16A34A" : rate >= 50 ? "#D97706" : "#DC2626", fontWeight: 700 }}>
                          {r.assigned > 0 ? `${rate}%` : "—"}
                        </td>
                        <td style={{ minWidth: 100 }}>
                          <div style={{ height: 8, background: "#E2EBF0", borderRadius: 4, overflow: "hidden" }}>
                            <div style={{ height: "100%", width: `${rate}%`, background: rate >= 80 ? "#16A34A" : rate >= 50 ? "#D97706" : "#DC2626", borderRadius: 4, transition: "width .5s" }} />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Delivery rate summary */}
        <div className="card">
          <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 16, color: "#0B1E3D", marginBottom: 20 }}>
            📊 Summary
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 16 }}>
            {[
              { label: "Delivery Rate", value: orders.length > 0 ? `${Math.round(((statusCounts.delivered || 0) / orders.length) * 100)}%` : "—", desc: "of all orders delivered", color: "#16A34A" },
              { label: "Most Popular Route", value: Object.entries(routeCounts).sort((a,b) => b[1]-a[1])[0]?.[0] || "—", desc: "highest volume route", color: "#00A896" },
              { label: "Most Popular Type", value: Object.entries(typeCounts).sort((a,b) => b[1]-a[1])[0]?.[0] || "—", desc: "most booked shipment type", color: "#00C6D7" },
              { label: "Total Riders", value: riders.length, desc: `${riders.filter(r => r.is_active).length} currently active`, color: "#0B1E3D" },
            ].map(s => (
              <div key={s.label} style={{ padding: "16px", background: "#F7FAFB", borderRadius: 10, border: "1px solid #E2EBF0" }}>
                <div style={{ fontSize: 11, color: "#9CA3AF", marginBottom: 6 }}>{s.label}</div>
                <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 22, color: s.color, textTransform: "capitalize", marginBottom: 4 }}>{s.value}</div>
                <div style={{ fontSize: 11, color: "#6B7280" }}>{s.desc}</div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}